import type { Permission, SearchResultDTO, SearchResultType } from '@hearth/shared';
import { Briefcase, Contact, type LucideIcon, Plus, StickyNote, Store, UserRound } from 'lucide-react';
import { NAV_SECTIONS } from './navigation';

export type PaletteItem = {
  id: string;
  label: string;
  sublabel: string | null;
  icon: LucideIcon;
  /** Where Enter/click navigates. */
  to: string;
  hint?: string;
};

export type PaletteGroup = { key: string; label: string; items: PaletteItem[] };

type Command = { id: string; label: string; to: string; icon: LucideIcon; keywords: string; hint?: string; permission?: Permission };

/** Static navigation commands: every nav destination plus "New lead" and "Account". */
const COMMANDS: Command[] = [
  ...NAV_SECTIONS.flatMap((section) =>
    section.items.map<Command>((item) => ({
      id: `go:${item.key}`,
      label: `Go to ${item.label}`,
      to: item.to,
      icon: item.icon,
      keywords: `${item.label} ${item.key} ${item.keywords ?? ''}`,
      hint: item.hotkey,
      permission: item.permission,
    })),
  ),
  { id: 'new:lead', label: 'New lead', to: '/leads/new', icon: Plus, keywords: 'create add business restaurant pizzeria' },
  { id: 'go:account', label: 'Go to Account', to: '/account', icon: UserRound, keywords: 'profile password' },
];

const RESULT_GROUPS: Record<SearchResultType, { label: string; icon: LucideIcon }> = {
  business: { label: 'Leads', icon: Store },
  contact: { label: 'Contacts', icon: Contact },
  opportunity: { label: 'Opportunities', icon: Briefcase },
  note: { label: 'Notes', icon: StickyNote },
};

const RESULT_ORDER: SearchResultType[] = ['business', 'contact', 'opportunity', 'note'];

/** Lead page for a search hit; opportunity hits open the Opportunities tab. */
export function searchResultPath(result: SearchResultDTO): string {
  const base = `/leads/${encodeURIComponent(result.businessId)}`;
  return result.type === 'opportunity' ? `${base}?tab=opportunities` : base;
}

/**
 * Palette content for the typed text: search hits grouped by type (Leads, Contacts, Opportunities, Notes),
 * then the static commands whose label/keywords contain every typed word. Empty query → commands only.
 */
export function buildPaletteGroups(
  query: string,
  results: readonly SearchResultDTO[],
  can: (permission: Permission) => boolean,
): PaletteGroup[] {
  const groups: PaletteGroup[] = [];
  const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);

  if (words.length > 0) {
    for (const type of RESULT_ORDER) {
      const { label, icon } = RESULT_GROUPS[type];
      const items = results
        .filter((r) => r.type === type)
        .map<PaletteItem>((r) => ({ id: `${r.type}:${r.id}`, label: r.title, sublabel: r.subtitle, icon, to: searchResultPath(r) }));
      if (items.length > 0) groups.push({ key: type, label, items });
    }
  }

  const commands = COMMANDS.filter((c) => {
    if (c.permission && !can(c.permission)) return false;
    const haystack = `${c.label} ${c.keywords}`.toLocaleLowerCase();
    return words.every((w) => haystack.includes(w));
  }).map<PaletteItem>((c) => ({ id: c.id, label: c.label, sublabel: null, icon: c.icon, to: c.to, hint: c.hint }));
  if (commands.length > 0) groups.push({ key: 'commands', label: words.length > 0 ? 'Commands' : 'Go to', items: commands });

  return groups;
}
